'use client';

import { Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  ChevronDownIcon,
  HomeIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { getAvatarUrlWithFallback } from '../../utils/avatarUtils';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function AdminNavigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) return null;

  return (
    <nav className="bg-gray-900 shadow-md relative z-40 border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
               <Link to="/admin-tools" className="flex items-center space-x-2">
                <ShieldCheckIcon className="h-8 w-8 text-blue-400" />
                <span className="font-bold text-lg text-white">Admin Console</span>
              </Link>
            </div>
            
            <div className="hidden md:ml-10 md:flex md:items-baseline md:space-x-4">
              <Link
                to="/admin-tools"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium flex items-center"
              >
                <Squares2X2Icon className="h-4 w-4 mr-2" />
                Dashboard
              </Link>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium flex items-center border border-gray-700 hover:border-gray-600 transition-colors"
            >
              <HomeIcon className="h-4 w-4 mr-2" />
              Back to App
            </Link>

            <div className="h-6 w-px bg-gray-700 mx-2" />

            <Menu as="div" className="relative">
              <Menu.Button className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-blue-500">
                <img
                  className="h-8 w-8 rounded-full border border-gray-700"
                  src={getAvatarUrlWithFallback(user.photoURL)}
                  alt={user.displayName || user.email || 'Admin'}
                  width={32}
                  height={32}
                />
                <ChevronDownIcon className="ml-2 h-4 w-4 text-gray-400" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                      Logged in as<br/>
                      <span className="font-semibold text-gray-900 truncate block">{user.email}</span>
                    </div>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleSignOut}
                          className={classNames(
                            active ? 'bg-gray-100' : '',
                            'flex w-full text-left px-4 py-2 text-sm text-gray-700 items-center'
                          )}
                        >
                          <ArrowLeftOnRectangleIcon className="h-4 w-4 mr-2 text-gray-400" />
                          Sign out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </nav>
  );
}
